import { useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"

import TextField from "@/components/form/textField/TextField"
import BasicButton from "@/components/button/BasicButton"
import FormulaDisplay from "./FormulaDisplay"
import MetricList from "./MetricList"
import CalculatorPad from "./CalculatorPad"
import TestFormula from "./TestFormula"

import { useMetricList } from "@/hooks/api/useMetricQuery"
import { CalculatedMetric } from "@/types/setting"
import { validateFormula, validateName } from "../utils/formulaValidation"
import { extractMetricKeys, tokenizeFormula, tokensToFormula } from "../utils/formulaTokenizer"

interface Props {
  metric: CalculatedMetric
  onChange: (metric: CalculatedMetric) => void
  onSave: () => void
  onDelete: () => void
  isSaving: boolean
  isNew: boolean
  existingNames: string[]
}

export default function MetricDetail({
  metric,
  onChange,
  onSave,
  onDelete,
  isSaving,
  isNew,
  existingNames,
}: Props) {
  const { t } = useTranslation()
  const { data: allMetricsData } = useMetricList()

  const [nameError, setNameError] = useState<string>()
  const [formulaError, setFormulaError] = useState<string>()
  const [metricSearch, setMetricSearch] = useState("")

  // metric이 변경될 때 에러 초기화
  useEffect(() => {
    setNameError(undefined)
    setFormulaError(undefined)
    setMetricSearch("")
  }, [metric.idfMetric])

  // 사용 가능한 메트릭 목록 (Calculated Metric 제외 - 순환 참조 방지)
  const availableMetrics = useMemo(() => {
    if (!allMetricsData) return []
    return allMetricsData.filter((metric) => !metric.key.startsWith("calculated"))
  }, [allMetricsData])

  // 수식에서 사용된 메트릭 key 목록
  const usedMetricKeys = extractMetricKeys(metric.formula)

  // 이름 변경 (검증은 blur 시점에)
  const handleNameChange = (value: string) => {
    onChange({ ...metric, name: value })
    // 입력 중 에러 표시 제거
    if (nameError) setNameError(undefined)
  }

  // 이름 blur 시 검증
  const handleNameBlur = () => {
    const result = validateName(metric.name, existingNames)
    setNameError(result.error ? t(result.error, { ...result.errorParams }) : undefined)
  }

  // 메트릭 추가
  const handleAddMetric = (metricKey: string) => {
    if (usedMetricKeys.length >= 10) return // 최대 10개 제한
    const newFormula = metric.formula ? `${metric.formula} ${metricKey}` : metricKey
    handleFormulaChange(newFormula)
  }

  // 연산자/숫자 추가
  const handleAddToken = (token: string) => {
    const newFormula = metric.formula + token
    handleFormulaChange(newFormula)
  }

  // 수식 변경 (검증은 Save 시점에만)
  const handleFormulaChange = (formula: string) => {
    onChange({ ...metric, formula })
    // 입력 중 에러 표시 제거 (이전 에러가 남아있으면 클리어)
    if (formulaError) setFormulaError(undefined)
  }

  // Clear
  const handleClear = () => {
    onChange({ ...metric, formula: "" })
    setFormulaError(undefined)
  }

  // Backspace (숫자/연산자는 한 글자씩, 메트릭은 토큰 단위로 삭제)
  const handleBackspace = () => {
    const formula = metric.formula
    if (!formula) return

    // 끝 공백 제거 후 마지막 문자 확인
    const trimmedEnd = formula.trimEnd()
    if (!trimmedEnd) {
      handleFormulaChange("")
      return
    }

    const lastChar = trimmedEnd[trimmedEnd.length - 1]

    // 숫자나 소수점이면 한 글자만 삭제
    if (/[\d.]/.test(lastChar)) {
      handleFormulaChange(trimmedEnd.slice(0, -1))
      return
    }

    // 연산자나 괄호면 해당 문자 + 앞 공백 삭제
    if (/[+\-*/()]/.test(lastChar)) {
      const withoutOp = trimmedEnd.slice(0, -1).trimEnd()
      handleFormulaChange(withoutOp)
      return
    }

    // 그 외는 메트릭 → 마지막 토큰 전체 삭제
    const tokens = tokenizeFormula(formula)
    if (tokens.length === 0) return
    tokens.pop()
    const newFormula = tokensToFormula(tokens)
    handleFormulaChange(newFormula)
  }

  // 저장 전 최종 검증
  const handleSave = () => {
    const nameResult = validateName(metric.name, existingNames)
    if (!nameResult.isValid) {
      setNameError(t(nameResult.error!, { ...nameResult.errorParams }))
      return
    }

    const formulaResult = validateFormula(metric.formula)
    if (!formulaResult.isValid) {
      setFormulaError(t(formulaResult.error!, { ...formulaResult.errorParams }))
      return
    }

    onSave()
  }

  const isValid = !nameError && !formulaError && metric.name.trim() && metric.formula.trim()
  const isMaxMetricsReached = usedMetricKeys.length >= 10

  return (
    <div className="MetricDetailCont">
      {/* 이름 입력 */}
      <div className="section">
        <span className="section-label">{t("metricName")}</span>
        <TextField
          value={metric.name}
          onChange={(e) => handleNameChange((e.target as HTMLInputElement).value)}
          onBlur={handleNameBlur}
          error={!!nameError}
          helperText={nameError}
          slotProps={{
            htmlInput: { maxLength: 35 },
          }}
        />
      </div>

      {/* 수식 표시 */}
      <div className="section formula-section">
        <span className="section-label">{t("formula")}</span>
        <div className={`formula-box ${formulaError ? "error" : ""}`}>
          <FormulaDisplay formula={metric.formula} availableMetrics={availableMetrics} />
        </div>
        {formulaError && <span className="formula-error">{formulaError}</span>}
      </div>

      {/* 메트릭 선택 + 계산기 패드 */}
      <div className="input-area">
        <MetricList
          metrics={availableMetrics}
          searchValue={metricSearch}
          onSearchChange={setMetricSearch}
          onSelect={handleAddMetric}
          totalCount={availableMetrics.length}
          disabled={isMaxMetricsReached}
        />

        <CalculatorPad
          onAddToken={handleAddToken}
          onClear={handleClear}
          onBackspace={handleBackspace}
        />
      </div>

      {/* 테스트 수식 (접이식) */}
      <TestFormula formula={metric.formula} availableMetrics={availableMetrics} />

      {/* 액션 버튼 */}
      <div className="actions">
        <div>
          {!isNew && (
            <BasicButton type="danger-outlined" size="md" onClick={onDelete}>
              {t("button.delete", "Delete")}
            </BasicButton>
          )}
        </div>
        <BasicButton
          type="primary-contained"
          size="md"
          onClick={handleSave}
          disabled={!isValid || isSaving}
          isLoading={isSaving}>
          {t("button.save", "Save")}
        </BasicButton>
      </div>
    </div>
  )
}
